import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography } from '@mui/material';
import { useConfirm, type ConfirmOptions } from 'material-ui-confirm';
import { translate as t } from 'hooks/useTranslations';
import { neutral, primary } from '_lex/_foundations/phxColors';

const TITLE_ID = 'discard-workflow-changes-modal-title';
const BODY_ID = 'discard-workflow-changes-modal-body';

const paperSx = {
  borderRadius: 2,
  bgcolor: neutral[25],
  maxWidth: 500,
};

const confirmTitleSx = {
  p: 0,
  pt: 5,
  px: 4,
  pb: 1,
  mb: 3,
  textAlign: 'center',
  fontWeight: 600,
  color: primary[900],
  fontSize: '16px',
  lineHeight: 1.6,
  letterSpacing: '-0.6px',
};

const confirmContentSx = {
  p: 0,
  pt: 0,
  px: 4,
  pb: 3,
  textAlign: 'center',
};

const bodyTypographySx = {
  color: neutral[500],
  lineHeight: 1.2,
  textAlign: 'center',
  m: 0,
};

const confirmActionsSx = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  gap: 2,
  py: 4,
  px: 3,
  width: '100%',
  boxSizing: 'border-box',
  borderTop: `1px solid ${neutral[100]}`,
  '& > :not(style) ~ :not(style)': { ml: 0 },
};

const keepEditingButtonSx = {
  flex: 1,
  minWidth: 0,
  textTransform: 'none',
  fontWeight: 600,
  borderColor: neutral[200],
  color: neutral[700],
  bgcolor: neutral[25],
  '&:hover': {
    borderColor: neutral[300],
    bgcolor: neutral[50],
  },
};

const discardButtonSx = {
  flex: 1,
  minWidth: 0,
  textTransform: 'none',
  fontWeight: 600,
  bgcolor: primary[900],
  color: neutral[25],
  boxShadow: 'none',
  '&:hover': {
    bgcolor: primary[800],
    boxShadow: 'none',
  },
};

function getDiscardWorkflowLeaveConfirmOptions(): ConfirmOptions {
  return {
    title: t('performanceReporting.reviewWorkflows.new.discardModal.title'),
    content: (
      <Typography
        id={BODY_ID}
        variant='labelSm'
        component='p'
        sx={bodyTypographySx}
      >
        {t('performanceReporting.reviewWorkflows.new.discardModal.body')}
      </Typography>
    ),
    confirmationText: t('performanceReporting.reviewWorkflows.new.discardModal.yesDiscard'),
    cancellationText: t('performanceReporting.reviewWorkflows.new.discardModal.keepEditing'),
    dialogProps: {
      maxWidth: 'md',
      fullWidth: true,
      scroll: 'body',
      'aria-labelledby': TITLE_ID,
      'aria-describedby': BODY_ID,
      PaperProps: {
        elevation: 8,
        sx: paperSx,
      },
    },
    titleProps: {
      id: TITLE_ID,
      component: 'h2',
      sx: confirmTitleSx,
    },
    contentProps: {
      sx: confirmContentSx,
    },
    dialogActionsProps: {
      sx: confirmActionsSx,
    },
    confirmationButtonProps: {
      variant: 'contained',
      size: 'small',
      sx: discardButtonSx,
    },
    cancellationButtonProps: {
      variant: 'outlined',
      size: 'small',
      color: 'neutral',
      sx: keepEditingButtonSx,
    },
  };
}

export function useDiscardWorkflowLeaveConfirm(isDirty = false, onBeforeNavigate?: () => void): () => void {
  const confirm = useConfirm();
  const navigate = useNavigate();

  return useCallback(() => {
    const leave = () => {
      onBeforeNavigate?.();
      navigate('..');
    };
    if (!isDirty) {
      leave();
      return;
    }
    confirm(getDiscardWorkflowLeaveConfirmOptions())
      .then(leave)
      .catch(() => {
        // User chose to keep editing (or closed without confirming).
      });
  }, [confirm, isDirty, navigate, onBeforeNavigate]);
}
